/**
 * Copied from https://github.com/jarrvis/stencil-click-outside/blob/master/src/index.ts
 * with some modifications
 */

import { Build as BUILD, ComponentInterface, getElement } from '@stencil/core';

declare type ClickOutsideDecorator = (target: ComponentInterface, propertyKey: string) => void;

declare interface ClickOutsideOptions {
  triggerEvents?: string;
  exclude?: string;
}

const ClickOutsideOptionsDefaults: ClickOutsideOptions = {
  triggerEvents: 'click',
  exclude: '',
};

/**
 * Call this function as soon as the click outside of annotated method's host is done.
 * @example
 ```
 @ClickOutside()
 callback() {
  // this will run when click outside of element (host component) is done.
}
 ```
 */
export function ClickOutside(opt: ClickOutsideOptions = ClickOutsideOptionsDefaults): ClickOutsideDecorator {
  return (proto: ComponentInterface, methodName: string) => {
    // this is to resolve the 'compiler optimization issue':
    // lifecycle events not being called when not explicitly declared in at least one of components from bundle
    (BUILD as any).cmpDidLoad = true;

    const { componentDidLoad, disconnectedCallback } = proto;

    proto.componentDidLoad = function () {
      const host = getElement(this);
      const method = this[methodName];
      registerClickOutside(this, host, method, opt);
      return componentDidLoad && componentDidLoad.call(this);
    };

    proto.disconnectedCallback = function () {
      const host = getElement(this);
      const method = this[methodName];
      removeClickOutside(this, host, method, opt);
      return disconnectedCallback && disconnectedCallback.call(this);
    };
  };
}

// Cache for event handlers (needed for removal)
const listeners = [];

/**
 * Register callback function for HTMLElement to be executed when user clicks outside of element.
 * @example
 ```
 <span
 ref={spanEl => registerClickOutside(this, spanEl, () => this.test())}>
 Hello, World!
 </span>;
 ```
 */
export function registerClickOutside(
  component: ComponentInterface,
  element: HTMLElement,
  callback: (data: any) => void,
  opt: ClickOutsideOptions = ClickOutsideOptionsDefaults,
): void {
  const list = (e: Event) => {
    initClickOutside(e, component, element, callback);
  };

  if (!listeners[callback.toString()]) {
    listeners[callback.toString()] = [];
  }

  listeners[callback.toString()].push({
    listener: list,
    element: element,
  });

  listeners.push({
    [callback.toString()]: list,
  });

  getTriggerEvents(opt).forEach(triggerEvent => {
    window.addEventListener(triggerEvent, list, false);
  });
}

function getTriggerEvents(opt: ClickOutsideOptions): Array<string> {
  if (opt.triggerEvents) {
    return opt.triggerEvents.split(',').map(e => e.trim());
  }
  return ['click'];
}

/**
 * Remove click outside callback function for HTMLElement.
 */
export function removeClickOutside(_: ComponentInterface, element: HTMLElement, callback: (data: any) => void, opt: ClickOutsideOptions = ClickOutsideOptionsDefaults): void {
  const toRemoveListener = listeners[callback.toString()].find(item => element === item.element);
  // keep only remaining listeners
  listeners[callback.toString()] = [...listeners[callback.toString()].filter(item => item !== toRemoveListener)];

  if (!toRemoveListener) {
  } else {
    getTriggerEvents(opt).forEach(triggerEvent => {
      window.removeEventListener(triggerEvent, toRemoveListener.listener, false);
    });
  }
}

function initClickOutside(event: Event, component: ComponentInterface, element: HTMLElement, callback: (data: any) => void) {
  const [target, ...rest] = [event.composedPath()[0] as HTMLElement, ...(event.composedPath().slice(1) as HTMLElement[])];
  let contains = false;
  for (const el of rest) {
    if (el === element) {
      contains = true;
      break;
    }
  }

  if (!contains) {
    callback.call(component, { target });
  }
}
